'use client'

import { IconX, IconMenu2 } from '@tabler/icons-react'
import type { Header, Footer } from '@/payload-types'
import NextLink from 'next/link'
import { Suspense, useCallback, useEffect, useState } from 'react'
import { ClassValue } from 'clsx'
import { usePathname } from 'next/navigation'

import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer'
import { CMSLink } from '@/components/payload/Link'
import { getHostnameWithLocale, localeMap } from '@/utilities/getLocale'
import { useLocale } from '@/providers/Locale'
import { Socials } from '@/components/frontend/Socials'
import { SearchForm } from '@/components/frontend/SearchForm'
import { Copyright } from '@/components/frontend/Copyright'
import { getEditionTitle } from '@/providers/Locale/LocaleSelector'
import { cn } from '@/utilities/ui'
import { SearchParamsOnLoad } from '@/components/frontend/SearchParamsOnLoad'
import { Locale } from '@/providers/Locale/types'

interface HamburgerProps {
    header: Header
    footer: Footer
    classes?: Partial<Record<'trigger' | 'triggerIcon', ClassValue>>
}

export function Hamburger({ header, classes, footer }: HamburgerProps) {
    const { locale } = useLocale()
    const pathname = usePathname()
    const [hostname, setHostname] = useState('#')

    const [open, setOpen] = useState(false)

    const navItems = header?.navItems || []
    const footerItems = footer?.navItems || []

    useEffect(() => {
        setOpen(false)
    }, [pathname, setOpen])

    useEffect(() => {
        setHostname(window.location.host)
    }, [setHostname])

    const onSearchParamsLoad = useCallback(() => {
        setOpen(false)
    }, [setOpen])

    return (
        <>
            <Suspense>
                <SearchParamsOnLoad onload={onSearchParamsLoad} />
            </Suspense>
            <Drawer direction='right' open={open} onOpenChange={setOpen}>
                <DrawerTrigger className={cn(classes?.trigger)} aria-label='Open menu'>
                    <IconMenu2 className={cn({ 'w-5 h-5': !classes?.triggerIcon }, classes?.triggerIcon)} />
                </DrawerTrigger>
                <DrawerContent>
                    <div className='overflow-y-auto h-full flex flex-col'>
                        <DrawerHeader className='flex justify-between items-center border-b-2 border-accent py-2 px-0 gap-4'>
                            <DrawerTitle className='pl-2 text-accent'>Menu</DrawerTitle>
                            <SearchForm classes={{ container: 'flex-grow md:hidden' }} />
                            <DrawerClose aria-label='Close menu'>
                                <IconX className='w-10 h-10 text-primary' stroke={1} />
                            </DrawerClose>
                        </DrawerHeader>
                        <nav className='uppercase border-b-2 border-accent tracking-wide py-2'>
                            {navItems.map(({ link }, i) => (
                                <CMSLink key={i} {...link} appearance='link' className='font-bold block p-2 hover:no-underline hover:text-accent' />
                            ))}
                        </nav>
                        <nav className='border-b-2 border-accent p-2'>
                            <div className='uppercase text-sm font-bold mb-2 tracking-wide'>{getEditionTitle(locale)}</div>
                            {Object.entries(localeMap)
                                .filter(([code]) => code !== locale)
                                .sort(([codeA], [codeB]) => codeA.localeCompare(codeB))
                                .map(([code, display]) => (
                                    <NextLink
                                        key={code}
                                        href={`//${getHostnameWithLocale(hostname, code as Locale)}`}
                                        className='flex items-center justify-start my-2 hover:text-accent'
                                    >
                                        <div className='rounded-full p-4 border-accent border-2 inline-block relative mr-2'>
                                            <span className='absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2'>{code}</span>
                                        </div>
                                        {display}
                                    </NextLink>
                                ))}
                        </nav>
                        <DrawerFooter className='p-0 m-0 md:mt-auto'>
                            <nav className='border-b-2 md:border-t-2 border-accent pl-2'>
                                {footerItems.map(({ link }, i) => {
                                    return <CMSLink key={i} {...link} appearance='link' className='block p-2 hover:no-underline hover:text-accent' />
                                })}
                            </nav>
                            <nav className='pl-2'>
                                <Socials
                                    variant='monochrome'
                                    links={{
                                        facebook: 'https://facebook.com/quartznews',
                                        twitter: 'https://twitter.com/qz',
                                        instagram: 'https://instagram.com/qz',
                                        youtube: 'https://www.youtube.com/channel/UC9f78Z5hgtDt0n8JWyfBk8Q',
                                        rss: '/rss',
                                    }}
                                    size={20}
                                />
                                <Copyright className='mt-3' />
                            </nav>
                        </DrawerFooter>
                    </div>
                </DrawerContent>
            </Drawer>
        </>
    )
}
